import React from 'react';
import { CatButton, CatCard, CatTextLink } from 'components/blocks-components';
import { Conditional } from '@cat-ecom/pcc-components';
import Image from 'next/image';
import cx from 'classnames';
import styles from './product-relative.module.scss';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import {
  IdealerInfo,
  IuserStoreLocationData
} from '../product-reusable-card/product.type';
import { CartResponse } from 'services/cart/types';
import { MergedPriceAndAvailabilityResponse } from 'services/dealerPriceAvailability/types';
import { AvailabilitySection } from '../related-parts/availabiity-section';
import { PriceSection } from '../related-parts/price-section';
import { AddToCartSection } from '../related-parts/add-to-cart-section';
import { useTranslations } from 'next-intl';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';

interface RelatedPartsMobileProps {
  part: MergedPriceAndAvailabilityResponse;
  userStoreLocationData: IuserStoreLocationData;
  locale: string;
  isHeadlessPDPEnabled: boolean;
  cartDetails: CartResponse;
  storeLocationId: string;
  catcorp: boolean;
  handleSelectItemGa: (part: MergedPriceAndAvailabilityResponse) => void;
  dealerInformation: IdealerInfo;
  availRemoveInterimStockFlag: boolean;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled;
  contactDealerBtn?: React.ReactNode;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const RelatedPartsMobile: React.FC<RelatedPartsMobileProps> = (props) => {
  const {
    part,
    userStoreLocationData,
    locale,
    isHeadlessPDPEnabled,
    cartDetails,
    storeLocationId,
    catcorp,
    handleSelectItemGa,
    dealerInformation,
    availRemoveInterimStockFlag,
    isShowSourcingDetailEnabled,
    isDeliveryEnabled,
    contactDealerBtn,
    isRTL = false,
    currencyLdFeatureFlag = false
  } = props;

  const {
    partNumber = '',
    name = '',
    imageURL = '',
    currency = '',
    replacementParts = [],
    enableAddToCart = false
  } = part;

  const t = useTranslations();
  const dealerLocatorDrawer = useDealerLocatorDrawer();

  return (
    <CatCard
      data-testid="card-border"
      className={`pt-2 ${styles['product-relative-mobile']}`}
      key={partNumber}
    >
      <div className="card-body">
        <div className="d-flex ">
          <Image
            src={imageURL || PRODUCT_FALLBACK_IMAGE}
            loading="lazy"
            width={80}
            height={80}
            unoptimized
            alt={partNumber}
            className={styles['product-relative-mobile__productImage']}
            data-testid="related-product-image"
          />
          <div
            className={cx(
              styles['product-relative-mobile__product-info'],
              'd-flex flex-column px-2'
            )}
          >
            <CatTextLink
              data-testid="related-product-link"
              href={partNumber}
              className={cx('p-0')}
              onClick={() => handleSelectItemGa(part)}
            >
              {`${partNumber}: ${name}`}
            </CatTextLink>
            <Conditional
              test={!catcorp && replacementParts?.length === 0}
              fallback={
                <div className="mt-4 border-0 d-flex justify-content-end px-2">
                  <CatButton
                    size="md"
                    onClick={() => dealerLocatorDrawer.openDrawer()}
                  >
                    {t('CAT_CHECK_PRICE')}
                  </CatButton>
                </div>
              }
            >
              <PriceSection
                data-testid="price-section"
                part={part}
                currency={currency}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            </Conditional>
            {!catcorp && (
              <Conditional
                test={enableAddToCart}
                fallback={
                  <div
                    className="mt-4 border-0 d-flex justify-content-end px-2"
                    data-testid="component__contactDealerActions"
                  >
                    {contactDealerBtn}
                  </div>
                }
              />
            )}

            <AvailabilitySection
              part={part}
              catcorp={catcorp}
              replacementParts={replacementParts}
              enableAddToCart={enableAddToCart}
              locale={locale}
              userStoreLocationData={userStoreLocationData}
              dealerInformation={dealerInformation}
              availRemoveInterimStockFlag={availRemoveInterimStockFlag}
              isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
              isDeliveryEnabled={isDeliveryEnabled}
            />
            <Conditional
              test={
                !catcorp && replacementParts?.length === 0 && enableAddToCart
              }
            >
              <AddToCartSection
                part={part}
                cartDetails={cartDetails}
                storeLocationId={storeLocationId}
                currency={currency}
                isHeadlessPDPEnabled={isHeadlessPDPEnabled}
                catcorp={catcorp}
              />
            </Conditional>
          </div>
        </div>
      </div>
    </CatCard>
  );
};

export default RelatedPartsMobile;
